import React from "react"
import { useQuery } from "@apollo/client/react"
import { usePageProps } from "@love-irish/application-page-context"
import type { CollectionSection } from "@love-irish/types"

import DAILY_SCENES from "./daily_scenes.graphql"
import { DailyScene } from "./index"

export interface ConnectedDailySceneProps {
  /** Collection global id (gid). Preferred — pass the daily-scenes collection's id. */
  collectionId?: string
  /** Fallback lookup by slug when no gid is passed. */
  slug?: string
  greeting?: React.ComponentProps<typeof DailyScene>["greeting"]
  /**
   * Ambient "TV" mode (fills the viewport, forces carousel autoplay). Defaults to the server's
   * fullscreen render context (page props), so the /tv route gets it for free; pass explicitly to override.
   */
  tv?: boolean
  /** Rendered when the collection resolves to no usable sections (e.g. not set up yet). */
  fallback?: React.ReactNode
  className?: string
}

/**
 * ConnectedDailyScene
 *
 * Fetches a collection (by gid, or slug fallback) and hands its sections straight to DailyScene —
 * the presentational component reads the server CollectionSection fields directly, so there's no
 * remapping. Empty sections are dropped so a section with no records never renders.
 */
export const ConnectedDailyScene: React.FC<ConnectedDailySceneProps> = ({
  collectionId,
  slug,
  greeting,
  tv,
  fallback,
  className,
}) => {
  // Default TV mode to the server's fullscreen render context (the /tv route sets @fullscreen), so the
  // page doesn't thread a `tv` prop; an explicit `tv` still wins.
  const fullscreen = usePageProps().renderContext === "fullscreen"
  const tvMode = tv ?? fullscreen

  const { data, loading } = useQuery(DAILY_SCENES, {
    variables: { id: collectionId, slug: collectionId ? undefined : slug },
    skip: !collectionId && !slug,
  })

  const collection = (data as any)?.collection ?? null
  const allSections = collection?.sections ?? []
  const sections: CollectionSection[] = allSections.filter(
    (section: CollectionSection) =>
      (section.collectionItems ?? []).some((item) => item?.resource)
  )

  // While loading, render nothing (avoids a fallback flash); once settled with no sections, show fallback.
  if (sections.length === 0) return loading ? null : <>{fallback ?? null}</>

  return (
    <DailyScene
      sections={sections}
      greeting={greeting}
      tv={tvMode}
      className={className}
      collection={collection}
    />
  )
}

export default ConnectedDailyScene
